import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  IconButton,
  Chip,
  Typography,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Stack,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import { FishingBoat, QueryParams } from '../types';
import api, { API_BASE_URL } from '../services/api';

const BoatList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // 현재 페이지의 전체 URL 생성
  const getCurrentUrl = () => {
    const search = searchParams.toString();
    return `/boats${search ? `?${search}` : ''}`;
  };

  const [boats, setBoats] = useState<FishingBoat[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [region, setRegion] = useState('');
  const [dataSource, setDataSource] = useState('');
  const [isVerified, setIsVerified] = useState<boolean | ''>('');
  const [isActive, setIsActive] = useState<boolean | ''>('');

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedBoats, setSelectedBoats] = useState<number[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [boatToDelete, setBoatToDelete] = useState<number | null>(null);

  // URL에서 페이지 정보 읽기
  const page = parseInt(searchParams.get('page') || '0');
  const rowsPerPage = parseInt(searchParams.get('limit') || '20');

  // 편집 페이지에서 돌아왔을 때 스크롤 위치 복원
  useEffect(() => {
    const returnedData = location.state?.returnedData;
    const returnedScroll = location.state?.scrollPosition;

    // 스크롤 위치 복원
    if (returnedScroll !== undefined) {
      setTimeout(() => window.scrollTo(0, returnedScroll), 100);
    }

    // 캐시된 데이터가 있으면 복원 (취소 버튼 클릭 시)
    if (returnedData) {
      console.log('편집 페이지에서 돌아옴 - 캐시된 데이터 사용');
      setBoats(returnedData.boats);
      setTotal(returnedData.total);

      // state 제거 (뒤로가기 시 재사용 방지)
      window.history.replaceState({}, document.title);
      return;
    }
  }, [location.state]);

  useEffect(() => {
    // 편집 페이지에서 돌아왔으면 fetchBoats 건너뛰기
    if (location.state?.returnedData) {
      console.log('편집 페이지에서 돌아옴 - API 호출 건너뛰기');
      return;
    }

    console.log('BoatList fetchBoats 실행');

    // URL 파라미터에서 필터 값 읽어오기
    const searchParam = searchParams.get('search');
    const regionParam = searchParams.get('region');
    const dataSourceParam = searchParams.get('dataSource');
    const isVerifiedParam = searchParams.get('isVerified');
    const isActiveParam = searchParams.get('isActive');

    if (searchParam) setSearch(searchParam);
    if (regionParam) setRegion(regionParam);
    if (dataSourceParam) setDataSource(dataSourceParam);
    if (isVerifiedParam) setIsVerified(isVerifiedParam === 'true');
    if (isActiveParam) setIsActive(isActiveParam === 'true');

    fetchBoats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, searchParams.get('dataSource'), searchParams.get('isVerified'), searchParams.get('search'), searchParams.get('region'), searchParams.get('isActive')]);

  const fetchBoats = async () => {
    try {
      setIsLoading(true);
      setError('');

      const params: QueryParams = {
        page: page + 1,
        limit: rowsPerPage,
      };

      // URL 파라미터에서 직접 읽어서 사용
      const searchParam = searchParams.get('search');
      const regionParam = searchParams.get('region');
      const dataSourceParam = searchParams.get('dataSource');
      const isVerifiedParam = searchParams.get('isVerified');
      const isActiveParam = searchParams.get('isActive');

      if (searchParam) params.search = searchParam;
      if (regionParam) params.region = regionParam;
      if (dataSourceParam) params.dataSource = dataSourceParam as any;
      if (isVerifiedParam !== null) params.isVerified = isVerifiedParam === 'true';
      if (isActiveParam !== null) params.isActive = isActiveParam === 'true';

      console.log('API 요청 파라미터:', params);
      const response = await api.getBoats(params);
      setBoats(response.items);
      setTotal(response.meta.total);
    } catch (err: any) {
      console.error('Failed to fetch boats:', err);
      setError(err.response?.data?.message || '낚싯배 목록을 불러오는데 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = () => {
    const params = new URLSearchParams(searchParams);
    params.set('page', '0');

    // 검색어 추가
    if (search) {
      params.set('search', search);
    } else {
      params.delete('search');
    }

    // 지역 추가
    if (region) {
      params.set('region', region);
    } else {
      params.delete('region');
    }

    // 데이터 소스 추가
    if (dataSource) {
      params.set('dataSource', dataSource);
    } else {
      params.delete('dataSource');
    }

    // 검증 상태 추가
    if (isVerified !== '') {
      params.set('isVerified', String(isVerified));
    } else {
      params.delete('isVerified');
    }

    // 활성 상태 추가
    if (isActive !== '') {
      params.set('isActive', String(isActive));
    } else {
      params.delete('isActive');
    }

    setSearchParams(params);
  };

  const handleReset = () => {
    setSearch('');
    setRegion('');
    setDataSource('');
    setIsVerified('');
    setIsActive('');
    setSearchParams({});
  };

  const handleDelete = async (id: number) => {
    try {
      await api.deleteBoat(id);
      setDeleteDialogOpen(false);
      setBoatToDelete(null);
      fetchBoats();
    } catch (err: any) {
      console.error('Failed to delete boat:', err);
      setError(err.response?.data?.message || '낚싯배 삭제에 실패했습니다.');
    }
  };

  const handleBatchDelete = async () => {
    if (selectedBoats.length === 0) return;

    try {
      await api.batchDeleteBoats(selectedBoats);
      setSelectedBoats([]);
      fetchBoats();
    } catch (err: any) {
      console.error('Failed to batch delete:', err);
      setError(err.response?.data?.message || '일괄 삭제에 실패했습니다.');
    }
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedBoats(boats.map((boat) => boat.id));
    } else {
      setSelectedBoats([]);
    }
  };

  const handleSelectOne = (id: number) => {
    const selectedIndex = selectedBoats.indexOf(id);
    let newSelected: number[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedBoats, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedBoats.slice(1));
    } else if (selectedIndex === selectedBoats.length - 1) {
      newSelected = newSelected.concat(selectedBoats.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedBoats.slice(0, selectedIndex),
        selectedBoats.slice(selectedIndex + 1)
      );
    }

    setSelectedBoats(newSelected);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          낚싯배 관리
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/boats/create')}
        >
          낚싯배 추가
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            label="검색"
            placeholder="배 이름, 선주명, 모항"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            sx={{ minWidth: 250 }}
            size="small"
          />
          <TextField
            label="지역"
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            sx={{ minWidth: 120 }}
            size="small"
          />
          <FormControl sx={{ minWidth: 150 }} size="small">
            <InputLabel>데이터 소스</InputLabel>
            <Select
              value={dataSource}
              label="데이터 소스"
              onChange={(e) => setDataSource(e.target.value)}
            >
              <MenuItem value="">전체</MenuItem>
              <MenuItem value="manual">수동 입력</MenuItem>
              <MenuItem value="crawled">크롤링</MenuItem>
              <MenuItem value="imported">가져오기</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 120 }} size="small">
            <InputLabel>검증 상태</InputLabel>
            <Select<string>
              value={isVerified === '' ? '' : isVerified ? 'true' : 'false'}
              label="검증 상태"
              onChange={(e) => {
                const val = e.target.value as string;
                setIsVerified(val === '' ? '' : val === 'true');
              }}
            >
              <MenuItem value="">전체</MenuItem>
              <MenuItem value="true">검증됨</MenuItem>
              <MenuItem value="false">미검증</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 120 }} size="small">
            <InputLabel>활성 상태</InputLabel>
            <Select<string>
              value={isActive === '' ? '' : isActive ? 'true' : 'false'}
              label="활성 상태"
              onChange={(e) => {
                const val = e.target.value as string;
                setIsActive(val === '' ? '' : val === 'true');
              }}
            >
              <MenuItem value="">전체</MenuItem>
              <MenuItem value="true">활성</MenuItem>
              <MenuItem value="false">비활성</MenuItem>
            </Select>
          </FormControl>
          <Button variant="contained" startIcon={<SearchIcon />} onClick={handleSearch}>
            검색
          </Button>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleReset}>
            초기화
          </Button>
        </Box>
      </Paper>

      {selectedBoats.length > 0 && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'primary.light' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body1">
              {selectedBoats.length}개 선택됨
            </Typography>
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleBatchDelete}
            >
              선택 삭제
            </Button>
          </Box>
        </Paper>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : isMobile ? (
        <Stack spacing={1.5}>
          {boats.length === 0 ? (
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 4 }}>
              검색 결과가 없습니다
            </Typography>
          ) : boats.map((boat) => {
            const firstImage = boat.images && boat.images.length > 0 ? boat.images[0] : null;
            const imageUrl = firstImage ? `${API_BASE_URL}/uploads/boats/${boat.id}/${firstImage.filename}` : null;
            return (
              <Card key={boat.id} sx={{ cursor: 'pointer' }} onClick={() => navigate(`/boats/edit/${boat.id}`, { state: { from: getCurrentUrl() } })}>
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Stack direction="row" spacing={1.5}>
                    <Checkbox
                      checked={selectedBoats.indexOf(boat.id) !== -1}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => handleSelectOne(boat.id)}
                      sx={{ p: 0.5, alignSelf: 'flex-start' }}
                    />
                    {imageUrl ? (
                      <Box component="img" src={imageUrl} sx={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 1, flexShrink: 0 }} onError={(e: any) => { e.target.style.display = 'none'; }} />
                    ) : (
                      <Box sx={{ width: 72, height: 72, bgcolor: 'grey.200', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Typography variant="caption" color="text.secondary">없음</Typography>
                      </Box>
                    )}
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary">#{boat.id}</Typography>
                      <Typography variant="body2" fontWeight={600}>{boat.name}</Typography>
                      {boat.homePort && <Typography variant="caption" color="primary.main">📍 {boat.homePort}</Typography>}
                      <Typography variant="caption" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {boat.address || boat.region || '-'}
                      </Typography>
                      {boat.targetSpecies && boat.targetSpecies.length > 0 && (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5, gap: 0.5 }}>
                          {boat.targetSpecies.slice(0, 4).map((sp: string) => (
                            <Chip key={sp} label={sp} size="small" sx={{ height: 18, fontSize: 10 }} />
                          ))}
                        </Stack>
                      )}
                      <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                        {!boat.isActive && <Chip label="비활성" color="default" size="small" sx={{ height: 18, fontSize: 10 }} />}
                        {boat.isVerified && <Chip label="검증" color="success" size="small" sx={{ height: 18, fontSize: 10 }} />}
                      </Stack>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, newPage) => {
              const params = new URLSearchParams(searchParams);
              params.set('page', newPage.toString());
              setSearchParams(params);
            }}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              const params = new URLSearchParams(searchParams);
              params.set('limit', e.target.value);
              params.set('page', '0');
              setSearchParams(params);
            }}
            rowsPerPageOptions={[10, 20, 50]}
            labelRowsPerPage=""
          />
        </Stack>
      ) : (
        <Paper>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={boats.length > 0 && selectedBoats.length === boats.length}
                      indeterminate={selectedBoats.length > 0 && selectedBoats.length < boats.length}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell>이미지</TableCell>
                  <TableCell>ID</TableCell>
                  <TableCell>이름</TableCell>
                  <TableCell>지역</TableCell>
                  <TableCell>모항</TableCell>
                  <TableCell>주소</TableCell>
                  <TableCell>소개 첫줄</TableCell>
                  <TableCell>인승</TableCell>
                  <TableCell>대상어종</TableCell>
                  <TableCell>연락처</TableCell>
                  <TableCell>데이터 소스</TableCell>
                  <TableCell>상태</TableCell>
                  <TableCell align="right">작업</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {boats.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={13} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        검색 결과가 없습니다
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  boats.map((boat) => {
                    const firstImage = boat.images && boat.images.length > 0 ? boat.images[0] : null;
                    const imageUrl = firstImage
                      ? `${API_BASE_URL}/uploads/boats/${boat.id}/${firstImage.filename}`
                      : null;

                    return (
                      <TableRow
                        key={boat.id}
                        hover
                        selected={selectedBoats.indexOf(boat.id) !== -1}
                        onClick={(e) => {
                          // 체크박스, 버튼 클릭은 제외
                          if ((e.target as HTMLElement).closest('input[type="checkbox"], button')) {
                            return;
                          }
                          const currentUrl = getCurrentUrl();
                          navigate(`/boats/edit/${boat.id}`, {
                            state: {
                              from: currentUrl,
                              listData: { boats, total },
                              scrollPosition: window.scrollY
                            }
                          });
                        }}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedBoats.indexOf(boat.id) !== -1}
                            onChange={() => handleSelectOne(boat.id)}
                          />
                        </TableCell>
                        <TableCell>
                          {imageUrl ? (
                            <Box
                              component="img"
                              src={imageUrl}
                              alt={boat.name}
                              sx={{
                                width: 60,
                                height: 60,
                                objectFit: 'cover',
                                borderRadius: 1,
                              }}
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                            />
                          ) : (
                            <Box
                              sx={{
                                width: 60,
                                height: 60,
                                bgcolor: 'grey.200',
                                borderRadius: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <Typography variant="caption" color="text.secondary">
                                이미지 없음
                              </Typography>
                            </Box>
                          )}
                        </TableCell>
                        <TableCell>{boat.id}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {boat.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 200, fontSize: '0.75rem' }}>
                          {boat.region}
                        </Typography>
                      </TableCell>
                      <TableCell>{boat.homePort || '-'}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 250, fontSize: '0.75rem' }}>
                          {boat.address || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 300, fontSize: '0.75rem' }}>
                          {boat.description ? boat.description.split('\n')[0] : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {boat.maxPassengers ? `${boat.maxPassengers}인승` : '-'}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 200, fontSize: '0.75rem' }}>
                          {boat.targetSpecies && boat.targetSpecies.length > 0
                            ? boat.targetSpecies.join(', ')
                            : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>{boat.phones && boat.phones.length > 0 ? boat.phones.join(', ') : '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={
                            boat.dataSource === 'manual'
                              ? '수동'
                              : boat.dataSource === 'crawled'
                              ? '크롤링'
                              : '가져오기'
                          }
                          size="small"
                          color={boat.dataSource === 'manual' ? 'success' : 'info'}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {boat.isVerified && (
                            <Chip label="검증" size="small" color="warning" />
                          )}
                          {boat.isActive && (
                            <Chip label="활성" size="small" color="success" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <IconButton
                          size="small"
                          onClick={() => {
                            const currentUrl = getCurrentUrl();
                            navigate(`/boats/edit/${boat.id}`, {
                              state: {
                                from: currentUrl,
                                listData: { boats, total },
                                scrollPosition: window.scrollY
                              }
                            });
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            setBoatToDelete(boat.id);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, newPage) => {
              const params = new URLSearchParams(searchParams);
              params.set('page', newPage.toString());
              setSearchParams(params);
            }}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              const params = new URLSearchParams(searchParams);
              params.set('limit', e.target.value);
              params.set('page', '0');
              setSearchParams(params);
            }}
            labelRowsPerPage="페이지당 행 수:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} / 총 ${count}`}
          />
        </Paper>
      )}

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} fullScreen={isMobile}>
        <DialogTitle>낚싯배 삭제</DialogTitle>
        <DialogContent>
          <Typography>정말로 이 낚싯배를 삭제하시겠습니까?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>취소</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => boatToDelete && handleDelete(boatToDelete)}
          >
            삭제
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BoatList;
